import { appNavigateToLogin, getPlatformInfo } from '@/services/h5Service';
import {
  findEnterpriseCodeByMobile,
  findEnvMessage,
  getMainSystemEnvInfo,
  restartEnterpriseService,
} from '@/services/service';
import { history, useModel } from '@@/exports';
import { useParams } from '@umijs/max';
import { Button, message, Result, Space, Spin, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CloudServerOutlined,
  DatabaseOutlined,
  PauseCircleFilled,
  PlayCircleOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { appendRegCodeToUrl, getStoredRegCode } from '@/util/regCode';
import './ProcessEnv.less';

type RecoveryState = 'idle' | 'stopped' | 'restarting' | 'restored';

interface EnvironmentActivityInfo {
  lastUsedAt?: string | number;
  autoStoppedAt?: string | number;
}

const formatActivityTime = (value?: string | number) => {
  if (!value) {
    return '暂无记录';
  }
  const normalizedValue = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  const activityTime = moment(normalizedValue);
  return activityTime.isValid() ? activityTime.format('YYYY-MM-DD HH:mm') : '暂无记录';
};

const ProcessEnv = (props) => {
  const { fromPage, mobile, skipUrlCheck } = props;
  const { initialState } = useModel('@@initialState');
  const enterpriseCode = useParams().enterpriseCode || props.enterpriseCode;
  const [envMessages, setEnvMessages] = useState([]); //表单对象值
  const [envSuccess, setEnvSuccess] = useState(false); //表单对象值
  const [enterpriseId, setEnterpriseId] = useState<number>();
  const [restarting, setRestarting] = useState(false);
  const [recoveryState, setRecoveryState] = useState<RecoveryState>('idle');
  const [recoveryError, setRecoveryError] = useState<string>();
  const [activityInfo, setActivityInfo] = useState<EnvironmentActivityInfo>({});
  const timerRef = React.useRef<any>();
  const platformInfoRef = useRef();
  const enterpriseCodeRef = useRef<string>();
  const recoveryModeRef = useRef(false);
  const isH5 =
    (isMobile && history.location.pathname.startsWith('/h5')) ||
    history.location.pathname.startsWith('/edatlink-admin/h5');

  const renderEnvMessages = () => (
    <div style={{ textAlign: 'left', marginTop: 20 }}>
      <Space direction={'vertical'}>
        {Array.isArray(envMessages) && envMessages.length > 0 ? (
          envMessages.map((item: any, index: number) => {
            return (
              <span key={index}>
                {!envSuccess && index === envMessages.length - 1 && (
                  <Spin indicator={<SyncOutlined spin />} size="default" />
                )}{' '}
                <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item}</span>
              </span>
            );
          })
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Spin indicator={<SyncOutlined spin />} size="default"></Spin>
            <span> 正在处理中...</span>
          </div>
        )}
      </Space>
    </div>
  );

  const clearPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const redirectToEnterprise = async (
    enterpriseCodeTemp: string,
    currentPlatformInfo: any = platformInfoRef.current,
  ) => {
    if (fromPage === 'admin') {
      return;
    }
    let host = location.host.replace('platform', '');
    if (isMobile) {
      if (currentPlatformInfo?.appName === 'edatlink_app') {
        const regCode = getStoredRegCode();
        await appNavigateToLogin({
          actionKey: 'autoLogin',
          actionGroupKey: 'autoLogin',
          data: { enterpriseCode: enterpriseCodeTemp, ...(regCode ? { regCode } : {}) },
        });
        return;
      }
      location.href = location.origin + '/edatlink-admin/app/guide';
      return;
    }
    if (host === 'iswan.cc') {
      host = 'prod.iswan.cc';
    }
    const loginUrl = host.includes(enterpriseCodeTemp)
      ? 'https://' +
        host +
        '/edatlink-admin/user/login?autoLogin=Y&enterpriseCode=' +
        enterpriseCodeTemp
      : 'https://' +
        enterpriseCodeTemp +
        '.' +
        host +
        '/edatlink-admin/user/login?autoLogin=Y&enterpriseCode=' +
        enterpriseCodeTemp;
    location.href = appendRegCodeToUrl(loginUrl);
  };

  const _findEnvMessage = async (
    enterpriseCodeTemp: string,
    currentPlatformInfo: any = platformInfoRef.current,
  ) => {
    // 如果设置了 skipUrlCheck，则跳过 URL 检查
    if (!skipUrlCheck) {
      if (
        location.href.indexOf('/prepareEnv') === -1 &&
        location.href.indexOf('/processEnv') === -1 &&
        location.href.indexOf('/base/enterprise/perEnterprise') === -1 &&
        location.href.indexOf('/h5/prepareEnv') === -1 &&
        location.href.indexOf('/h5/processEnv') === -1
      ) {
        console.log('stop timerRef');
        clearPolling();
        return false;
      }
    }
    console.log('enterpriseCode', enterpriseCodeTemp);

    if (enterpriseCodeTemp) {
      let envInfo = await getMainSystemEnvInfo({ enterpriseCode: enterpriseCodeTemp });
      console.log({ envInfo });
      setEnterpriseId(envInfo?.enterpriseId);
      // 停用详情由环境接口返回，页面只展示时间，不参与停用条件判断。
      setActivityInfo({
        lastUsedAt: envInfo?.lastUsedAt,
        autoStoppedAt: envInfo?.autoStoppedAt,
      });
      if (envInfo?.autoStopped === 'Y') {
        recoveryModeRef.current = true;
        setRecoveryState('stopped');
        setEnvSuccess(false);
        setEnvMessages([]);
        clearPolling();
        return false;
      }
      const isRecoveryRestarting = recoveryModeRef.current || envInfo?.autoRestarting === 'Y';
      if (
        isRecoveryRestarting &&
        (envInfo?.autoRestarting === 'Y' || envInfo?.start === 'processing')
      ) {
        recoveryModeRef.current = true;
        setRecoveryState('restarting');
        setEnvSuccess(false);
      } else if (envInfo && envInfo.envSuccess === 'Y' && envInfo.autoRestarting !== 'Y') {
        setEnvSuccess(true);
        clearPolling();
        if (recoveryModeRef.current) {
          setRecoveryState('restored');
        } else {
          setTimeout(async () => {
            await redirectToEnterprise(enterpriseCodeTemp, currentPlatformInfo);
          }, 3000);
        }
        return false;
      }
      console.log('enterpriseCode', enterpriseCodeTemp);
      let res = await findEnvMessage({ enterpriseCode: enterpriseCodeTemp });
      if (Array.isArray(res)) {
        setEnvMessages(res);
      }
      return true;
    }
    return false;
  };

  const startPolling = (enterpriseCodeTemp: string) => {
    clearPolling();
    timerRef.current = setInterval(async () => {
      await _findEnvMessage(enterpriseCodeTemp, platformInfoRef.current);
    }, 2000);
  };

  useEffect(() => {
    const fetchData = async () => {
      platformInfoRef.current = await getPlatformInfo();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      let enterpriseCodeTemp = enterpriseCode;
      if (enterpriseCodeTemp === 'iyb') {
        enterpriseCodeTemp = await findEnterpriseCodeByMobile({
          mobile: mobile || initialState?.currentUser?.mobile,
        });
      }
      enterpriseCodeRef.current = enterpriseCodeTemp;
      const shouldPoll = await _findEnvMessage(enterpriseCodeTemp, platformInfoRef.current);
      if (shouldPoll) {
        startPolling(enterpriseCodeTemp);
      }
    };
    fetchData();
    return clearPolling;
  }, []);

  const restartService = async () => {
    if (!enterpriseId) {
      return;
    }
    recoveryModeRef.current = true;
    setRecoveryState('restarting');
    setRecoveryError(undefined);
    setRestarting(true);
    try {
      await restartEnterpriseService({ enterpriseId });
      if (enterpriseCodeRef.current) {
        startPolling(enterpriseCodeRef.current);
      }
      message.success('恢复请求已提交，正在启动原有服务');
    } catch (error: any) {
      clearPolling();
      setRecoveryState('stopped');
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errorMessage ||
        error?.data?.message ||
        error?.message ||
        '恢复请求失败，请稍后重试';
      setRecoveryError(errorMessage);
      message.error(errorMessage);
    } finally {
      setRestarting(false);
    }
  };

  const enterWorkspace = async () => {
    if (enterpriseCodeRef.current) {
      await redirectToEnterprise(enterpriseCodeRef.current);
    }
  };

  // 三个恢复阶段使用同一页面骨架，避免轮询期间切回“创建环境”界面。
  if (recoveryState !== 'idle') {
    const recoveryContent = {
      stopped: {
        statusText: '已休眠',
        title: '后台服务已暂停运行',
        description:
          '后台服务当前处于休眠状态。系统仅会在企业尚未产生业务数据且连续 7 天无使用记录时自动休眠。',
        icon: <PauseCircleFilled />,
      },
      restarting: {
        statusText: '恢复中',
        title: '正在恢复企业服务',
        description: '系统正在重新启动原有后台并挂载业务数据，无需重新创建环境。',
        icon: <SyncOutlined spin />,
      },
      restored: {
        statusText: '运行中',
        title: '企业服务已恢复',
        description: '原有数据和配置已加载完成，后台服务已恢复正常运行。',
        icon: <CheckCircleFilled />,
      },
    }[recoveryState];

    return (
      <div className={`serviceRecoveryPage ${fromPage === 'admin' ? 'isEmbedded' : ''}`}>
        <main className={`serviceRecoveryPanel ${recoveryState}`} aria-live="polite">
          <header className="serviceRecoveryHeader">
            <div className="serviceRecoveryIcon" aria-hidden="true">
              {recoveryContent.icon}
            </div>
            <div className="serviceRecoveryHeading">
              <div className="serviceRecoveryContext">
                <span className="serviceRecoveryStatus">
                  <i />
                  {recoveryContent.statusText}
                </span>
                {(enterpriseCodeRef.current || enterpriseCode) && (
                  <span className="serviceRecoveryEnterprise">
                    企业编号 {enterpriseCodeRef.current || enterpriseCode}
                  </span>
                )}
              </div>
              <h1>{recoveryContent.title}</h1>
              <p>{recoveryContent.description}</p>
            </div>
          </header>

          {recoveryState === 'stopped' && (
            <>
              <section className="serviceRecoveryFacts" aria-label="服务休眠说明">
                <div className="serviceRecoveryFact">
                  <DatabaseOutlined />
                  <div>
                    <strong>业务数据已保留</strong>
                    <span>文件、配置与历史数据不会删除</span>
                  </div>
                </div>
                <div className="serviceRecoveryFact">
                  <CloudServerOutlined />
                  <div>
                    <strong>后台资源已释放</strong>
                    <span>休眠期间不再占用运行资源</span>
                  </div>
                </div>
                <div className="serviceRecoveryFact">
                  <SafetyCertificateOutlined />
                  <div>
                    <strong>可随时恢复使用</strong>
                    <span>恢复后继续使用原有工作环境</span>
                  </div>
                </div>
              </section>

              <dl className="serviceRecoveryDetails">
                <div>
                  <dt>最后使用时间</dt>
                  <dd>{formatActivityTime(activityInfo.lastUsedAt)}</dd>
                </div>
                <div>
                  <dt>进入休眠时间</dt>
                  <dd>{formatActivityTime(activityInfo.autoStoppedAt)}</dd>
                </div>
                <div>
                  <dt>自动休眠规则</dt>
                  <dd>无业务数据且连续 7 天无使用记录</dd>
                </div>
              </dl>
            </>
          )}

          {recoveryState === 'restarting' && (
            <section className="serviceRecoveryProgress" aria-label="服务恢复进度">
              <Spin indicator={<SyncOutlined spin />} size="large" />
              <div className="serviceRecoveryProgressText">
                <strong>正在启动原有后台</strong>
                <span>资源启动、数据挂载与服务检查正在依次进行</span>
              </div>
              <div className="serviceRecoveryProgressLine" aria-hidden="true">
                <span />
              </div>
            </section>
          )}

          {recoveryState === 'restored' && (
            <section className="serviceRecoveryComplete">
              <CheckCircleFilled />
              <div>
                <strong>数据与配置已加载完成</strong>
                <span>服务检查通过，可以继续进入企业工作台。</span>
              </div>
            </section>
          )}

          <footer className="serviceRecoveryActions">
            <div>
              <strong>
                {recoveryState === 'stopped' && '恢复通常需要 1-2 分钟'}
                {recoveryState === 'restarting' && '恢复请求已提交'}
                {recoveryState === 'restored' && '服务状态正常'}
              </strong>
              <span>
                {recoveryState === 'stopped' && '系统将重新挂载原有数据和配置'}
                {recoveryState === 'restarting' && '完成后即可重新进入工作台'}
                {recoveryState === 'restored' && '现在可以继续上次的工作'}
              </span>
            </div>
            {recoveryState === 'stopped' && (
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                loading={restarting}
                disabled={!enterpriseId}
                onClick={restartService}
              >
                恢复使用
              </Button>
            )}
            {recoveryState === 'restarting' && (
              <Button size="large" icon={<SyncOutlined spin />} disabled>
                正在恢复
              </Button>
            )}
            {recoveryState === 'restored' && (
              <Button
                type="primary"
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={enterWorkspace}
              >
                进入工作台
              </Button>
            )}
          </footer>

          {recoveryError && recoveryState === 'stopped' && (
            <div className="serviceRecoveryError" role="alert">
              {recoveryError}
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <>
      {/*<Button onClick={()=> {*/}
      {/*  appNavigateToLogin({ fromPage: '/h5/prepareEnv',  enterpriseCode: enterpriseCode || 'cloud7f' });*/}
      {/*}}>立即跳转到首页</Button>*/}

      {isMobile ? (
        <div>
          <div style={{ textAlign: 'center' }}>
            <Space direction={'vertical'}>
              <Result
                status={envSuccess ? 'success' : 'info'}
                title={
                  <Space direction={'vertical'}>
                    <Typography.Title
                      level={1}
                      style={{ margin: '20px 0px 20px 0px', fontSize: 20 }}
                    >
                      {envSuccess ? '产品环境构建完成' : '正在为您准备产品环境'}
                    </Typography.Title>
                    {envSuccess ? (
                      // <span>{fromPage === 'admin' ? '产品创建成功' : '产品创建成功,请重新进行登录'}</span>
                      <>
                        {fromPage === 'admin' && <span>产品创建成功</span>}
                        {fromPage !== 'admin' && isH5 ? (
                          <Space direction={'vertical'}>
                            <span>产品创建成功,即将返回App</span>
                          </Space>
                        ) : (
                          <Space direction={'vertical'}>
                            <span>请登录 PC 端进行使用</span>
                          </Space>
                        )}
                      </>
                    ) : (
                      <span>
                        <div style={{ fontSize: 22 }}>预计1-2分钟左右</div>
                        <div style={{ fontSize: 18 }}>请您耐心等待,请不要离开该页面!</div>
                      </span>
                    )}
                  </Space>
                }
                subTitle={renderEnvMessages()}
              />
            </Space>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ textAlign: 'center' }}>
            <Space direction={'vertical'}>
              <Result
                status={envSuccess ? 'success' : 'info'}
                title={
                  <Space direction={'vertical'}>
                    <Typography.Title level={1} style={{ margin: '20px 0px 20px 0px' }}>
                      {envSuccess ? '产品环境构建完成' : '正在为您准备产品环境'}
                    </Typography.Title>
                    {envSuccess ? (
                      <span>{fromPage === 'admin' ? '产品创建成功' : '即将跳转到产品首页'}</span>
                    ) : (
                      <span>预计1-2分钟左右</span>
                    )}
                  </Space>
                }
                subTitle={renderEnvMessages()}
              />
            </Space>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Space direction={'vertical'}>
              {/*<Image preview={false} width={'400px'}*/}
              {/*       src={'https://website-1306430916.cos.ap-shanghai.myqcloud.com/website/cloud7f_gzh.png'}/>*/}
              {/*<span>你可以访问 <a href={'https://iswan.cc'}>https://iswan.cc</a> 或 扫描二维码了解更多产品内容</span>*/}
              <span>
                你可以访问 <a href={'https://iswan.cc'}>https://iswan.cc</a> 了解更多产品内容
              </span>
            </Space>
          </div>
        </div>
      )}
    </>
  );
};

export default ProcessEnv;
